import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { Skeleton } from "@/components/base/Loading";
import LicenseCard from "@/pages/licenses/components/LicenseCard";
import LicenseStats from "@/pages/licenses/components/LicenseStats";
import RedeemPanel from "@/pages/licenses/components/RedeemPanel";
import RedemptionBatchBreakdown from "@/pages/licenses/components/RedemptionBatchBreakdown";
import ExpiryPlanner from "@/pages/licenses/components/ExpiryPlanner";
import LicenseTimeline from "@/pages/licenses/components/LicenseTimeline";
import { useMyLicenses, type License } from "@/pages/licenses/hooks/useMyLicenses";
import { useRedemptionBatches } from "@/pages/licenses/hooks/useRedemptionBatches";
import { daysUntil } from "@/lib/format";

type FilterKey = "all" | "active" | "expiring" | "expired" | "inactive";
type SourceFilterKey = "all" | "application" | "redeem";

const FILTERS: { key: FilterKey; labelKey: string }[] = [
  { key: "all", labelKey: "licenses.filterAll" },
  { key: "active", labelKey: "licenses.filterActive" },
  { key: "expiring", labelKey: "licenses.filterExpiring" },
  { key: "expired", labelKey: "licenses.filterExpired" },
  { key: "inactive", labelKey: "licenses.filterInactive" },
];

const SOURCE_FILTERS: { key: SourceFilterKey; labelKey: string; icon: string }[] = [
  { key: "all", labelKey: "licenses.sourceAll", icon: "ri-stack-line" },
  { key: "application", labelKey: "licenses.sourceApplication", icon: "ri-file-check-line" },
  { key: "redeem", labelKey: "licenses.sourceRedeem", icon: "ri-gift-2-line" },
];

function matchesFilter(license: License, filter: FilterKey): boolean {
  switch (filter) {
    case "active":
      return license.status === "active";
    case "expiring": {
      if (license.status !== "active" || !license.expires_at) return false;
      const days = daysUntil(license.expires_at);
      return days >= 0 && days <= 30;
    }
    case "expired":
      return license.status === "expired";
    case "inactive":
      return license.status === "suspended" || license.status === "revoked";
    default:
      return true;
  }
}

function matchesSource(license: License, source: SourceFilterKey): boolean {
  if (source === "all") return true;
  return (license.source ?? "application") === source;
}

export default function LicensesPage() {
  const { t } = useTranslation();
  const { licenses, loading, error, reload } = useMyLicenses();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilterKey>("all");

  // 「激活码核销占比」卡下钻数据：仅在切到「激活码核销」来源时按需加载，避免多余请求。
  const {
    batches: redemptionBatches,
    exportLogs: redemptionExportLogs,
    total: redemptionBatchTotal,
    loading: batchLoading,
    error: batchError,
    logExport: logRedemptionExport,
  } = useRedemptionBatches(licenses, sourceFilter === "redeem");

  const visible = useMemo(
    () => licenses.filter((license) => matchesFilter(license, filter) && matchesSource(license, sourceFilter)),
    [licenses, filter, sourceFilter],
  );

  // 状态按钮的计数：在「当前来源」范围内统计；来源按钮的计数：在「当前状态」范围内统计。
  const statusCounts = useMemo(() => {
    const base = licenses.filter((license) => matchesSource(license, sourceFilter));
    return {
      all: base.length,
      active: base.filter((item) => matchesFilter(item, "active")).length,
      expiring: base.filter((item) => matchesFilter(item, "expiring")).length,
      expired: base.filter((item) => matchesFilter(item, "expired")).length,
      inactive: base.filter((item) => matchesFilter(item, "inactive")).length,
    } as Record<FilterKey, number>;
  }, [licenses, sourceFilter]);

  const sourceCounts = useMemo(() => {
    const base = licenses.filter((license) => matchesFilter(license, filter));
    return {
      all: base.length,
      application: base.filter((item) => matchesSource(item, "application")).length,
      redeem: base.filter((item) => matchesSource(item, "redeem")).length,
    } as Record<SourceFilterKey, number>;
  }, [licenses, filter]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("licenses.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("licenses.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("licenses.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("licenses.subtitle")}
                </p>
              </div>
              <Link
                to="/apply"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                {t("licenses.applyNew")}
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {loading ? (
            <div className="flex flex-col gap-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {[0, 1, 2, 3, 4].map((item) => (
                  <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="mt-3 h-6 w-1/3" />
                  </div>
                ))}
              </div>
              {[0, 1, 2].map((item) => (
                <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="mt-4 h-14 w-full" />
                  <Skeleton className="mt-4 h-3 w-2/3" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("licenses.reload")}
              </button>
            </div>
          ) : (
            <>
              <RedeemPanel onRedeemed={reload} />
              <LicenseStats licenses={licenses} source={sourceFilter} onSelectSource={setSourceFilter} />

              {licenses.length > 0 && <ExpiryPlanner licenses={licenses} />}

              {licenses.length > 0 && <LicenseTimeline licenses={licenses} />}

              {sourceFilter === "redeem" && (
                <RedemptionBatchBreakdown
                  batches={redemptionBatches}
                  exportLogs={redemptionExportLogs}
                  total={redemptionBatchTotal}
                  loading={batchLoading}
                  error={batchError}
                  onLogExport={logRedemptionExport}
                />
              )}

              <div className="mt-8 mb-6 flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {FILTERS.map((item) => {
                    const active = item.key === filter;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setFilter(item.key)}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                          active
                            ? "bg-primary-500 text-background-50"
                            : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        {t(item.labelKey)}
                        <span className={active ? "text-background-200" : "text-foreground-400"}>
                          {statusCounts[item.key]}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-foreground-500">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-filter-3-line" />
                    </span>
                    {t("licenses.sourceLabel")}
                  </span>
                  {SOURCE_FILTERS.map((item) => {
                    const active = item.key === sourceFilter;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setSourceFilter(item.key)}
                        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                          active
                            ? "bg-accent-500 text-background-50"
                            : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className={item.icon} />
                        </span>
                        {t(item.labelKey)}
                        <span className={active ? "text-background-200" : "text-foreground-400"}>
                          {sourceCounts[item.key]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {visible.length === 0 ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                    <i className="ri-key-2-line text-2xl" />
                  </span>
                  <p className="text-sm font-medium text-foreground-800">
                    {licenses.length === 0 ? t("licenses.emptyTitle") : t("licenses.emptyTitleFiltered")}
                  </p>
                  <p className="max-w-sm text-xs text-foreground-500">
                    {licenses.length === 0 ? t("licenses.emptyDesc") : t("licenses.emptyDescFiltered")}
                  </p>
                  {licenses.length === 0 && (
                    <Link
                      to="/apply"
                      className="mt-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-send-plane-line" />
                      </span>
                      {t("licenses.emptyApply")}
                    </Link>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {visible.map((license) => (
                    <LicenseCard key={license.id} license={license} onRefresh={reload} />
                  ))}
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-secondary-50 px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-information-line text-lg" />
                </span>
                <p className="text-xs leading-relaxed text-foreground-700">
                  {t("licenses.tip")}
                </p>
              </div>
            </>
          )}
        </section>
      </main>

      <AppFooter />
    </div>
  );
}